import { useState, useRef, useEffect } from 'react'
import { Clock, RefreshCw, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { recheckDomainAvailabilityThunk } from '@/services/CredentialCapabilityService'
import type { DashboardDomainRegistryItem } from '@/types/DashboardDomainRegistry.types'

/**
 * UnavailableDomainInfoModal — §37b info modal for credential-aware
 * unavailable domain cards.
 *
 * Rendered by `DomainPickerBlock` when the user clicks the info icon on an
 * `availability.state === 'unavailable'` domain card (Slice 2.3 FE,
 * brand-guide §37b).
 *
 * Responsibilities:
 *   1. Show domain icon + displayName + server-authored `availability.userMessage`
 *      verbatim — FE NEVER authors copy from reason.
 *   2. Show "Last checked" relative time from `availability.lastChecked`.
 *   3. "Re-check availability" primary CTA — calls Slice 3.1 endpoint
 *      (`POST /v1/credentials/:credentialId/capability/probe`) via
 *      `recheckDomainAvailabilityThunk`.
 *   4. "Got it" tertiary dismiss.
 *   5. Inline error handling — never a toast (toasts render behind modal overlay).
 *
 * Era: dashboard-era only. Do not use on workflow-era surfaces.
 */

interface UnavailableDomainInfoModalProps {
  /**
   * The domain item to display. Must have `availability` set — callers are
   * responsible for guarding `availability !== undefined` before rendering
   * (see `DomainPickerBlock.tsx:460`). The component narrows the type
   * explicitly to avoid relying on the `!` non-null assertion.
   */
  domain: DashboardDomainRegistryItem & {
    availability: NonNullable<DashboardDomainRegistryItem['availability']>
  }
  /** Resolved domain icon emoji (from iconMap). */
  iconEmoji: string
  /** Whether the modal is open. */
  open: boolean
  /** Callback to close the modal. Returns focus to the info icon. */
  onClose: () => void
}

export const UnavailableDomainInfoModal = ({
  domain,
  iconEmoji,
  open,
  onClose,
}: UnavailableDomainInfoModalProps) => {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((s: RootState) => s.user.token)
  const credentialId = useSelector(
    (s: RootState) => s.dashboardCreation.credentialId,
  )

  const [isRechecking, setIsRechecking] = useState(false)
  const [recheckError, setRecheckError] = useState<string | null>(null)
  const [recheckResult, setRecheckResult] = useState<
    'available' | 'unavailable' | null
  >(null)

  // Store the auto-close timer handle so it can be cleared on unmount or
  // manual close before the 250ms fires (prevents double-close / late callback).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  // availability is guaranteed by the prop type narrowing above — no `!` needed.
  const availability = domain.availability

  const handleRecheck = async () => {
    // Defense-in-depth guard. Both `token` and `credentialId` absence disable
    // the button (disabled predicate matches these conditions), so this branch
    // is only reachable if the disabled attribute is bypassed programmatically.
    /* istanbul ignore next */
    if (!token || !credentialId) return
    setIsRechecking(true)
    setRecheckError(null)
    setRecheckResult(null)

    const outcome = await dispatch(
      recheckDomainAvailabilityThunk(
        token,
        credentialId,
        domain.pickerKey,
        domain.qualifiedDomainId,
      ),
    )

    setIsRechecking(false)

    if (outcome === 'available') {
      setRecheckResult('available')
      // Close the modal — the card transitions in place (§37b "On success").
      // Brief delay so the success state is perceptible on fast connections.
      // Timer is stored in a ref so unmount/manual-close can cancel it.
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current)
      }
      closeTimerRef.current = setTimeout(() => {
        onClose()
      }, 250)
    } else if (outcome === 'unavailable') {
      setRecheckResult('unavailable')
    } else {
      // outcome is an error string
      setRecheckError(outcome as string)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose()
    }
  }

  // Format the last-checked relative time.
  const lastCheckedFormatted = (() => {
    try {
      return formatDistanceToNow(new Date(availability.lastChecked), {
        addSuffix: true,
      })
    } catch {
      return 'recently'
    }
  })()

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[440px] p-0 overflow-hidden rounded-2xl gap-0"
        aria-describedby="unavailableModalDesc"
      >
        {/* Header — custom close button removed; DialogContent auto-renders its
            own absolutely-positioned X close button (dialog.tsx:57-60). Adding
            a custom one inside the header produced two stacked X icons.
            See Copilot finding #1 (PR #393 review pass). */}
        <div className="flex items-start p-5 pb-0 pr-12">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-[9px] bg-muted border border-border flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <span className="text-[18px]">{iconEmoji}</span>
            </div>
            <div>
              <DialogTitle className="text-[15px] font-bold text-primary leading-tight">
                {domain.displayName} not available
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your current CampMinder credential
              </p>
            </div>
          </div>
        </div>

        <Separator className="mt-4" />

        {/* Body */}
        <div className="px-5 pt-4">
          <p
            className="text-sm text-secondary leading-[1.65]"
            id="unavailableModalDesc"
          >
            {/* Server-authored copy — rendered verbatim. FE never branches on reason. */}
            {availability.userMessage ?? ''}
          </p>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
            <Clock size={12} aria-hidden="true" />
            Last checked {lastCheckedFormatted}
          </p>

          {/* Inline error / still-unavailable feedback — never a toast */}
          {recheckError !== null && (
            <p
              className="text-xs text-destructive mt-2"
              role="alert"
              aria-live="polite"
            >
              {recheckError}
            </p>
          )}
          {recheckResult === 'unavailable' && recheckError === null && (
            <p
              className="text-xs text-secondary mt-2"
              role="alert"
              aria-live="polite"
            >
              Still unavailable. Try again later or contact your admin.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 p-5">
          {/* Primary: Re-check availability */}
          <Button
            className="w-full font-semibold"
            onClick={handleRecheck}
            disabled={isRechecking || !credentialId || !token}
            aria-label={`Re-check availability for ${domain.displayName}`}
            aria-busy={isRechecking}
          >
            {isRechecking ? (
              <>
                <Loader2
                  size={16}
                  className="animate-spin mr-2"
                  aria-hidden="true"
                />
                Checking…
              </>
            ) : (
              <>
                <RefreshCw size={14} className="mr-2" aria-hidden="true" />
                Re-check availability
              </>
            )}
          </Button>

          {/* Secondary: Got it dismiss */}
          <Button variant="outline" className="w-full" onClick={onClose}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
